<template>
    <div>
        <v-card class="elevation-0">
            <div class="d-flex align-center">
                <v-card-title class="text-h6 font-weight-bold d-flex align-center">
                    {{ $vuetify.lang.t("$vuetify.access_control.ip_access") }}
                </v-card-title>
            </div>
            <v-divider></v-divider>

            <v-row class="mx-2 mb-3 align-center">
                <v-col cols="12" md="6">
                    <div>
                        <v-card class="pa-2" flat>
                            <strong>{{
                                $vuetify.lang.t("$vuetify.access_control.allowed_ips")
                            }}</strong>
                        </v-card>
                        <div class="ml-1">
                            <span class="info-icon">i</span><span class="text--secondary text-caption ml-2">{{ $vuetify.lang.t('$vuetify.access_control.ip_version_hint') }}</span>
                        </div>
                    </div>
                </v-col>
                <v-col cols="12" md="6">
                    <v-row class="justify-end align-center">
                        <Button :buttonType="FLOATING_ACTION_BUTTON" iconName="mdi-plus" size="x-small"
                            :buttonText="$vuetify.lang.t('$vuetify.common.create')" color="primary"
                            @onClick="createNewClick" :disabled="!$hasProjectAccessFor([$pRole.ADMIN])" />
                    </v-row>
                </v-col>
            </v-row>
            <div>
                <Loader v-if="isLoading" class="mr-2 mt-6" />
                <v-data-table v-else 
                    class="elevation-1" 
                    :headers="ipHeaders"
                    :items="ip_accesses"
                    :server-items-length="total" :page.sync="page" :items-per-page.sync="itemsPerPage"
                    :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :must-sort="true">
                    <template v-slot:[`item.title`]="{ item }">
                        {{ item.title || 'N/A' }}
                    </template>
                    <template v-slot:[`item.allowed_ip`]="{ item }">
                        {{ item.allowed_ip }}
                    </template>
                    <template v-slot:[`item.status`]="{ item }">
                        <v-chip :color="item.status === 'enabled' ? 'green' : 'red'" dark small>
                            {{ item.status === 'enabled' ? 'Enabled' : 'Disabled' }}
                        </v-chip>
                    </template>
                    <template v-slot:item.created_at="{ item }">
                        {{ item.created_at ? formatDateTime(item.created_at) : "N/A" }}
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon small v-bind="attrs" v-on="on" @click="editItem(item)"
                                    :disabled="!$hasProjectAccessFor([$pRole.ADMIN])">
                                    <v-icon small>mdi-pencil</v-icon>
                                </v-btn>
                            </template>
                            <span>Edit</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon small v-bind="attrs" v-on="on" @click="enableItem(item)"
                                    :disabled="!$hasProjectAccessFor([$pRole.ADMIN]) || item.status === 'enabled'"
                                    v-if="item.status === 'disabled'">
                                    <v-icon small>mdi-play</v-icon>
                                </v-btn>
                            </template>
                            <span>Enable</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon small v-bind="attrs" v-on="on" @click="disableItem(item)"
                                    :disabled="!$hasProjectAccessFor([$pRole.ADMIN]) || item.status === 'disabled'"
                                    v-if="item.status === 'enabled'">
                                    <v-icon small>mdi-stop</v-icon>
                                </v-btn>
                            </template>
                            <span>Disable</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon small v-bind="attrs" v-on="on" @click="deleteItem(item)"
                                    :disabled="!$hasProjectAccessFor([$pRole.ADMIN])">
                                    <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                            </template>
                            <span>Delete</span>
                        </v-tooltip>
                    </template>
                </v-data-table>

                <!-- Create/Edit Dialog -->
                <v-form ref="form" v-model="valid">
                    <v-dialog v-model="dialog" width="600" persistent>
                        <v-card>
                            <v-card-title class="primary--text font-weight-bold">
                                {{
                                    editMode
                                        ? $vuetify.lang.t("$vuetify.access_control.edit_ip_access")
                                        : $vuetify.lang.t("$vuetify.access_control.add_ip")
                                }}
                            </v-card-title>
                            <v-divider></v-divider>

                            <v-card-text class="pt-4">
                                <v-text-field v-model="title"
                                    :label="$vuetify.lang.t('$vuetify.access_control.ip_title')" outlined dense
                                    class="mb-4"></v-text-field>
                                <v-text-field v-model="ipAddress"
                                    :label="$vuetify.lang.t('$vuetify.access_control.ip_address')" :rules="[validateIp]"
                                    outlined dense placeholder="192.168.1.1 or 2001:0db8:85a3::8a2e:0370:7334"
                                    persistent-hint
                                    >
                                </v-text-field>
                            </v-card-text>

                            <v-divider></v-divider>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <Button class="red--text font-weight-bold" :buttonType="TEXT_BUTTON"
                                    :buttonText="$vuetify.lang.t('$vuetify.common.cancel_btn')" color="red"
                                    @onClick="closeDialog" />
                                <Button class="font-weight-bold" :buttonType="RAISED_BUTTON"
                                    :buttonText="$vuetify.lang.t('$vuetify.common.save_btn')"
                                    :disabled="!valid || !ipAddress" color="primary" @onClick="saveIpClick"
                                    :loading="isLoad" />
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-form>

                <DialogModal v-model="deleteDialog" :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
                    :message="$vuetify.lang.t('$vuetify.access_control.confirm_delete')"
                    :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
                    :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel_btn')" @onConfirmation="confirmDelete"
                    @onCancel="deleteDialog = false" />
            </div>
        </v-card>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { formatDateTime } from "@/helpers/helper";
import {
    FLOATING_ACTION_BUTTON,
    RAISED_BUTTON,
    TEXT_BUTTON
} from "@/components/common/button/_buttontypes";
import Button from "@/components/common/button/Button";
import Loader from "@/components/common/loader/Loader";
import DialogModal from "@/components/common/DialogModal";
import {
    CREATE_IP_ACCESS,
    GET_ALL_IP_ACCESS,
    UPDATE_IP_ACCESS,
    DELETE_IP_ACCESS
} from "@/store/_actiontypes";

export default {
    name: "IpAccessControl",
    components: {
        Button,
        Loader,
        DialogModal
    },
    data() {
        return {
            ipHeaders: [
                { text: this.$vuetify.lang.t("$vuetify.table_header.title"), value: "title", align: "center", sortable: true },
                { text: this.$vuetify.lang.t("$vuetify.table_header.ip"), value: "allowed_ip", align: "center", sortable: true },
                { text: this.$vuetify.lang.t("$vuetify.table_header.status"), value: "status", align: "center", sortable: true },
                { text: this.$vuetify.lang.t("$vuetify.table_header.created_at"), value: "created_at", align: "center", sortable: true },
                { text: this.$vuetify.lang.t("$vuetify.table_header.actions"), value: "actions", align: "center", sortable: false },
            ],
            FLOATING_ACTION_BUTTON,
            RAISED_BUTTON,
            TEXT_BUTTON,
            formatDateTime,
            isLoading: false,
            isLoad: false,
            valid: true,
            dialog: false,
            deleteDialog: false,
            editMode: false,
            title: "",
            ipAddress: "",
            ipToDelete: null,
            ip_accesses: [],
            page: 1,
            itemsPerPage: 10,
            total: 0,
            sortBy: 'created_at',
            sortDesc: true,
            debounceTimer: null
        };
    },
    computed: {
        ...mapState({
            selectedProject: state => state.project.selectedProject,
            user: state => state.account.user,
        }),
        projectId() {
            return this.selectedProject?.id;
        }
    },
    watch: {
        page() {
            this.fetchIpAccessData();
        },
        itemsPerPage() {
            this.page = 1;
            this.fetchIpAccessData();
        },
        sortBy() {
            this.fetchIpAccessData();
        },
        sortDesc() {
            this.fetchIpAccessData();
        }
    },
    created() {
        this.fetchIpAccessData();
    },
    methods: {
        ...mapActions("project", [CREATE_IP_ACCESS, GET_ALL_IP_ACCESS, UPDATE_IP_ACCESS, DELETE_IP_ACCESS]),

        async fetchIpAccessData() {
            this.isLoading = true;
            try {
                const perPage = this.itemsPerPage === -1 ? 1000 : this.itemsPerPage;
                const sortOrder = this.sortDesc ? 'desc' : 'asc';
                const response = await this.GET_ALL_IP_ACCESS({
                    project_id: this.projectId,
                    page: this.page,
                    per_page: perPage,
                    sort_by: this.sortBy,
                    sort_order: sortOrder
                });

                if (response.data) {
                    this.ip_accesses = Array.isArray(response.data.items) ? response.data.items : [];
                    this.total = response.data.total || 0;
                }
            } catch (error) {
                console.error("Error fetching IP access data:", error);
            } finally {
                this.isLoading = false;
            }
        },

        async enableItem(item) {
            this.isLoad = true;
            try {
                await this.UPDATE_IP_ACCESS({
                    project_id: this.projectId,
                    access_id: item.id,
                    title: item.title,
                    allowed_ip: item.allowed_ip,
                    status: 'enabled'
                });
                this.fetchIpAccessData();
            } catch (error) {
                console.error("Error enabling IP rule:", error);
            } finally {
                this.isLoad = false;
            }
        },

        async disableItem(item) {
            this.isLoad = true;
            try {
                await this.UPDATE_IP_ACCESS({
                    project_id: this.projectId,
                    access_id: item.id,
                    title: item.title,
                    allowed_ip: item.allowed_ip,
                    status: 'disabled'
                });
                this.fetchIpAccessData();
            } catch (error) {
                console.error("Error disabling IP rule:", error);
            } finally {
                this.isLoad = false;
            }
        },

        createNewClick() {
            this.resetForm();
            this.editMode = false;
            this.dialog = true;
        },

        editItem(item) {
            this.editMode = true;
            this.title = item.title || "";
            this.ipAddress = item.allowed_ip;
            this.ipToDelete = item;
            this.dialog = true;
        },

        deleteItem(item) {
            this.ipToDelete = item;
            this.deleteDialog = true;
        },

        async confirmDelete() {
            this.isLoad = true;
            try {
                await this.DELETE_IP_ACCESS({
                    project_id: this.projectId,
                    access_id: this.ipToDelete.id
                });
                if(this.page === 1) this.fetchIpAccessData();
                else this.page = 1;
                this.deleteDialog = false;
                this.ipToDelete = null;
            } catch (error) {
                console.error("Error deleting IP rule:", error);
            } finally {
                this.isLoad = false;
            }
        },

        closeDialog() {
            this.dialog = false;
            this.resetForm();
        },

        async saveIpClick() {
            if (this.$refs.form.validate()) {
                this.isLoad = true;
                try {
                    if (this.editMode) {
                        await this.UPDATE_IP_ACCESS({
                            project_id: this.projectId,
                            access_id: this.ipToDelete.id,
                            title: this.title,
                            allowed_ip: this.ipAddress,
                            status: this.ipToDelete.status
                        });
                    } else {
                        await this.CREATE_IP_ACCESS({
                            project_id: this.projectId,
                            title: this.title,
                            allowed_ip: this.ipAddress,
                            status: 'enabled'
                        });
                    }

                    this.dialog = false;
                    this.page = 1;
                    this.fetchIpAccessData();
                    this.resetForm();
                } catch (error) {
                    console.error("Error saving IP rule:", error);
                } finally {
                    this.isLoad = false;
                }
            }
        },

        resetForm() {
            this.title = "";
            this.ipAddress = "";
            this.editMode = false;
            this.ipToDelete = null;
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        validateIp(value) {
            if (!value) return this.$vuetify.lang.t("$vuetify.access_control.ip_required");

            // IPv4 regex
            const ipv4Regex =
                /^(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)$/;

            // IPv4 CIDR regex
            // const ipv4CidrRegex =
            //     /^(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\/([0-9]|[1-2][0-9]|3[0-2])$/;

            // IPv6 regex
            const ipv6Regex =
                /^(([0-9A-Fa-f]{1,4}:){7}[0-9A-Fa-f]{1,4}|([0-9A-Fa-f]{1,4}:){1,7}:|([0-9A-Fa-f]{1,4}:){1,6}:[0-9A-Fa-f]{1,4}|([0-9A-Fa-f]{1,4}:){1,5}(:[0-9A-Fa-f]{1,4}){1,2}|([0-9A-Fa-f]{1,4}:){1,4}(:[0-9A-Fa-f]{1,4}){1,3}|([0-9A-Fa-f]{1,4}:){1,3}(:[0-9A-Fa-f]{1,4}){1,4}|([0-9A-Fa-f]{1,4}:){1,2}(:[0-9A-Fa-f]{1,4}){1,5}|[0-9A-Fa-f]{1,4}:((:[0-9A-Fa-f]{1,4}){1,6})|:((:[0-9A-Fa-f]{1,4}){1,7}|:))$/;

            // IPv6 CIDR regex
            // const ipv6CidrRegex =
            //     /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|::|([0-9a-fA-F]{1,4}:){1,7}:)\/([0-9]|[1-9][0-9]|1[01][0-9]|12[0-8])$/;

            if (ipv4Regex.test(value) || ipv6Regex.test(value)) return true;
            return this.$vuetify.lang.t("$vuetify.access_control.invalid_ip");
        },
    }
};
</script>

<style>
.info-icon {
    display: inline-block;
    width: 18px;
    height: 18px;
    line-height: 18px;
    text-align: center;
    border-radius: 50%;
    background-color: #e0e0e0;
    color: #333;
    font-size: 12px;
    font-weight: bold;
    cursor: pointer;
    margin-left: 2px;
}
</style>